<template>
    <div class="p-2 widget my-2">
        <div class="row">
            <div class="col-12">
                <p class="text-uppercase font-weight-bold mb-0">Credit card on file</p>
                <a
                    v-if="organization.stripe_key"
                    @click="canEditClientFunding ? editCard() : null"
                    class="d-flex w-100 font-weight-bold justify-content-end"
                    :class="
                        canEditClientFunding
                            ? 'text-primary cursor-pointer'
                            : 'text-muted cursor-not-allowed'
                    "
                    >Edit</a
                >
                <p class="mt-2 small font-italic" v-if="!organization.stripe_key">
                    <i class="fa fa-exclamation-triangle text-warning mr-2"></i>
                    <span
                        >Your organization still needs to complete the Stripe setup.
                        <a
                            v-if="helpers._get(organization, 'pivot.role_id', false) === 1"
                            href="/control-center?view=stripe"
                            >Please visit the Control Panel.</a
                        >
                    </span>
                </p>
                <div class="px-4 py-2">
                    <div class="d-flex justify-content-between" v-if="patient.cardholder_name">
                        <p class="mb-0 text-capitalize">
                            Cardholder name:
                        </p>
                        <p class="mb-0 text-capitalize">{{ patient.cardholder_name }}</p>
                    </div>
                    <div class="d-flex justify-content-between">
                        <p class="mb-0 text-capitalize">
                            {{ patient.card_brand || "No card on file" }}
                        </p>
                        <pre>{{
                            patient.card_last_four ? `**** **** **** ${patient.card_last_four}` : ""
                        }}</pre>
                    </div>
                </div>
            </div>
        </div>
        <modal-component
            :visible="changeCardModalVisible"
            :id="'change-patient-card-modal'"
            :title="`Update ${patient.name}'s Credit Card`"
        >
            <div class="alert alert-warning">
                <p>
                    By checking the box below, you confirm that:
                </p>

                <ul>
                    <li>
                        You have the customer’s permission to initiate a payment or a series of
                        payments on their behalf
                    </li>
                    <li>The customer is aware of the anticipated frequency of payments</li>
                    <li>The customer is aware of how the payment amount will be determined</li>
                    <li>
                        Your customer authorizes <b>{{ organization.name }}</b> to send instructions
                        to the financial institution that issued the card entered below to take
                        payments from the card's account in accordance with the terms of the
                        customer's agreement with <b>{{ organization.name }}</b
                        >.
                    </li>
                </ul>

                <p><input type="checkbox" v-model="confirm" class="mr-3" />Confirm</p>
            </div>

            <div class="row">
                <div class="col-12 mt-3">
                    <input
                        id="cardholder-name"
                        v-model="cardholderName"
                        class="form-control"
                        placeholder="Cardholder name"
                    />
                </div>
                <div class="col-12 mt-3">
                    <div id="stripe-card-gt" class="form-control"></div>
                </div>
            </div>

            <div class="row">
                <div class="col-12 my-3 d-flex justify-content-end">
                    <button
                        :disabled="!confirm"
                        @click.prevent="createSetupIntent()"
                        id="update-card"
                        class="text-uppercase btn btn-sm btn-primary text-white"
                    >
                        Update card
                    </button>
                </div>
            </div>
        </modal-component>
    </div>
</template>

<script>
/*
https://stripe.com/docs/payments/cards/saving-cards-without-payment

https://stripe.com/docs/payments/cards/reusing-cards#mandates

Getting permission to save a card

Once you set up your payment flow to properly save a card with the Payment Intents or Setup Intents API, Stripe will mark any subsequent off-session payment as a merchant-initiated transaction (MIT) to reduce the need to authenticate. Merchant-initiated transactions require an agreement (also known as a “mandate”) between you and your customer. Add terms to your website or application on how you plan to process payments that your customer can opt into.

At a minimum, ensure that your terms cover the following:

* The customer’s permission to you initiating a payment or a series of payments on their behalf
* The anticipated frequency of payments (i.e., one-time or recurring)
* How the payment amount will be determined

Add text in your checkout flow that references the terms of the payment, for example:

I authorise [your business name] to send instructions to the financial institution that issued my card to take payments from my card account in accordance with the terms of my agreement with you.

*/

import ModalComponent from "../ui/ModalComponent";
import { helpers } from "../../plugins/helpers";
import { permissions } from "../../plugins/permissions";

export default {
    name: "PatientCreditCard",
    props: ["patient", "organization"],
    components: { ModalComponent },
    mixins: [permissions],
    data() {
        return {
            helpers,
            changeCardModalVisible: false,
            isStripeMounted: false,
            cardholderName: "",
            confirm: false,
        };
    },

    mounted() {
        this.initializeStripe();
        this.cardholderName = "";
        this.confirm = false;
    },

    watch: {
        organization() {
            this.initializeStripe();
        },
    },

    methods: {
        initializeStripe() {
            if (this.organization && this.organization.stripe_key && !this.isStripeMounted) {
                helpers.mountStripeElement("stripe-card-gt");
                this.isStripeMounted = true;
            }
        },

        editCard() {
            this.changeCardModalVisible = true;
        },

        createSetupIntent() {
            helpers.toggleSpinner("update-card");
            axios
                .post(
                    `/api/organizations/${this.organization.id}/payments/${this.patient.id}/setup`
                )
                .then(r => {
                    const intent = r.data;
                    this.handleCardSetup(intent);
                })
                .catch(e => {
                    helpers.toggleSpinner("update-card");
                    this.$swal(
                        "Error",
                        `Something went wrong updating ${this.patient.name}'s card.`,
                        "error"
                    );
                    console.error(e);
                });
        },

        handleCardSetup(intent) {
            stripe
                .handleCardSetup(intent.client_secret, card, {
                    payment_method_data: {
                        billing_details: { name: this.cardholderName },
                    },
                })
                .then(result => {
                    if (result.error) {
                        helpers.toggleSpinner("update-card");
                        this.$swal(
                            "Error",
                            `Something went wrong updating ${this.patient.name}'s card.`,
                            "error"
                        );
                    } else {
                        this.attachPaymentMethod(_.get(result, "setupIntent.payment_method", null));
                    }
                });
        },

        attachPaymentMethod(payment_method) {
            axios
                .post(
                    `/api/organizations/${this.organization.id}/payments/${
                        this.patient.id
                    }/payment_method`,
                    { payment_method }
                )
                .then(r => {
                    if (r.data.status === "success") {
                        const newCard = r.data.card;
                        this.updatePatient(payment_method, newCard);
                    } else {
                        this.$swal(
                            "Error",
                            `Something went wrong updating ${this.patient.name}'s card.`,
                            "error"
                        );
                    }
                })
                .catch(e => {
                    helpers.toggleSpinner("update-card");
                    console.error(e);
                    this.$swal(
                        "Error",
                        `Something went wrong updating ${this.patient.name}'s card.`,
                        "error"
                    );
                });
        },

        updatePatient(payment_method, newCard) {
            const payload = {
                stripe_payment_method: payment_method,
                card_brand: newCard.brand,
                card_last_four: newCard.last4,
                cardholder_name: this.cardholderName,
            };
            axios
                .patch(`/api/patients/${this.patient.id}`, payload)
                .then(r => {
                    helpers.toggleSpinner("update-card");
                    this.changeCardModalVisible = false;
                    this.confirm = false;
                    this.patient.cardholder_name = this.cardholderName;
                    this.patient.card_brand = newCard.brand;
                    this.patient.card_last_four = newCard.last4; //Updating props to display updated state before reload
                    this.cardholderName = "";
                    this.$emit("patient-saved");
                    this.$swal(
                        "Done!",
                        `${this.patient.name}'s card was successfully updated.`,
                        "success"
                    );
                })
                .catch(e => {
                    helpers.toggleSpinner("update-card");
                    console.error(e);
                });
        },
    },
};
</script>

<style scoped></style>
